import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useRouter } from 'next/compat/router';

import { FieldLabel } from '@puckeditor/core';
import type { CustomField } from '@puckeditor/core';

import { useCmsEnterpriseId } from '../cms-enterprise-context';
import type { DestinationOption } from './destination-picker-modal';
import { FIELD_CONTROL_RADIUS, fieldControlStyle } from './field-control-style';
import { segmentedControlCss, SEGMENTED_SELECTED_BG, SEGMENTED_SELECTED_COLOR } from './segmented-control-style';

// Lazy so `@apollo/client` and the generated `CmsPagesForDestinationPicker` document stay out of
// the storefront bundle. `fields` objects are reachable from the production renderer (cms/AGENTS.md
// "Known caveat, not yet solved"), so a static import here would ship Apollo client weight to shoppers.
// Enforced by `bundle-boundaries.test.ts`.
const DestinationPickerModal = lazy(() => import('./destination-picker-modal'));

export type DestinationFieldProps = {
  field: { label?: string };
  name: string;
  value: string | null | undefined;
  onChange: (value: string) => void;
  readOnly?: boolean;
};

export type DestinationMode = 'external' | 'internal';

/**
 * Infers initial mode from an absolute URI scheme or protocol-relative URL.
 * Root-relative storefront paths and fragments stay internal. Unsupported schemes open in external
 * mode so the author sees the validation error, rather than disguising them as internal paths.
 */
export function inferMode(value: string): DestinationMode {
  return /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(value.trim()) ? 'external' : 'internal';
}

export function validateDestination(value: string, mode: DestinationMode): string | null {
  if (value === '') return null;
  const error =
    mode === 'internal'
      ? 'Enter an internal path without whitespace, control characters or backslashes; use External URL for other destinations.'
      : 'Enter an absolute http:// or https:// URL, or a mailto: or tel: link.';
  // URL() repairs backslashes and strips controls, even when resolving relative destinations.
  // eslint-disable-next-line no-control-regex -- reject controls that URL() would silently strip
  if (/[\s\u0000-\u001f\u007f\\]/.test(value)) return error;
  if (mode === 'internal') {
    return inferMode(value) === 'internal' ? null : error;
  }

  try {
    const url = new URL(value);
    if (/^https?:\/\/[^/]/i.test(value) && url.hostname) return null;
    if ((url.protocol === 'mailto:' || url.protocol === 'tel:') && url.pathname) return null;
  } catch {
    return error;
  }
  return error;
}

/**
 * CSS custom properties with literal fallbacks, nesting `--puck-*` where the value should
 * follow Puck's chrome palette (cms/AGENTS.md gotcha #11).
 */
const colors = {
  border: 'var(--dcm-cms-destination-field-border-color, var(--puck-color-border, #d0d4d8))',
  accent: 'var(--dcm-cms-destination-field-accent-color, var(--puck-color-interactive, #004c3f))',
  accentContrast: 'var(--dcm-cms-destination-field-accent-contrast-color, var(--puck-color-white, #fff))',
  textMuted: 'var(--dcm-cms-destination-field-muted-text-color, var(--puck-color-text-secondary, #646d72))',
  error: 'var(--dcm-cms-destination-field-error-color, var(--puck-color-red-06, #d32f2f))',
};

const radii = {
  sm: 'var(--dcm-cms-destination-field-radius-sm, 4px)',
  md: 'var(--dcm-cms-destination-field-radius-md, 8px)',
  button: 'var(--dcm-cms-destination-field-button-radius, 7px)',
};

const columnStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 8 };

const TabRow = styled.div`
  ${segmentedControlCss}
  --_cms-segmented-bg: var(--dcm-cms-destination-field-tab-bg, var(--dcm-cms-segmented-bg, var(--puck-field-color-bg, #fff)));
  --_cms-segmented-border: var(
    --dcm-cms-destination-field-border-color,
    var(--dcm-cms-segmented-border-color, var(--puck-color-border, #d0d4d8))
  );
  --_cms-segmented-radius: var(
    --dcm-cms-destination-field-radius-md,
    var(--dcm-cms-segmented-radius, var(--puck-radius-m, 6px))
  );
  --_cms-segmented-selected-bg: var(--dcm-cms-destination-field-tab-active-bg, ${SEGMENTED_SELECTED_BG});
  --_cms-segmented-selected-color: var(--dcm-cms-destination-field-accent-color, ${SEGMENTED_SELECTED_COLOR});
`;

const chooseButtonStyle: React.CSSProperties = {
  border: 0,
  background: colors.accent,
  color: colors.accentContrast,
  borderRadius: radii.button,
  padding: '8px 12px',
  fontWeight: 750,
  fontSize: 13,
  cursor: 'pointer',
  width: '100%',
  whiteSpace: 'nowrap',
};

const inputStyle: React.CSSProperties = {
  ...fieldControlStyle,
  border: `1px solid ${colors.border}`,
  borderRadius: `var(--dcm-cms-destination-field-radius-sm, ${FIELD_CONTROL_RADIUS})`,
};

const hintStyle: React.CSSProperties = { marginTop: 0, fontSize: 12, color: colors.textMuted };

const errorStyle: React.CSSProperties = {
  color: colors.error,
  fontSize: 'var(--dcm-cms-destination-field-error-font-size, 12px)',
};

/**
 * Puck custom field for link destinations.
 *
 * Persists a single flat string. Invalid input stays local to the field, with an inline error;
 * only valid values (or an explicit clear) reach the page document.
 *
 * Active preview store authoritativeness contract:
 * The active router.query.cName preview store is intentionally authoritative. Website cName,
 * default dispensary, or sibling stores must NOT be substituted when router.query.cName is missing
 * or invalid. In multi-dispensary enterprises, substituting a website or sibling store would cause
 * cross-store route pollution or invalid links. When an active store cName is unavailable, internal
 * page picking is disabled/hidden, while manual text input and external URL mode remain fully functional.
 */
export function DestinationField({ field, name, value, onChange, readOnly }: DestinationFieldProps): JSX.Element {
  const router = useRouter();
  const rawCName = typeof router?.query.cName === 'string' ? router.query.cName.trim() : '';
  const exactStoreCName = rawCName.length > 0 ? rawCName : undefined;
  const enterpriseId = useCmsEnterpriseId();

  const fieldLabel = field.label ?? name;
  const resolvedValue = typeof value === 'string' ? value : '';
  const [mode, setMode] = useState<DestinationMode>(() => inferMode(resolvedValue));
  const [inputValue, setInputValue] = useState(resolvedValue);
  const [isOpen, setIsOpen] = useState(false);
  const chooseButtonRef = useRef<HTMLButtonElement>(null);

  // Undo, a picker selection, or a different component's saved value replaces any uncommitted draft.
  useEffect(() => {
    setInputValue(resolvedValue);
    if (resolvedValue !== '') setMode(inferMode(resolvedValue));
  }, [resolvedValue]);

  const error = validateDestination(inputValue, mode);
  const errorId = `destination-error-${name}`;
  const canChoosePage = Boolean(enterpriseId && exactStoreCName && !readOnly);

  const handleTextChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (readOnly) return;
      const nextValue = event.currentTarget.value;
      setInputValue(nextValue);
      if (!validateDestination(nextValue, mode)) onChange(nextValue);
    },
    [mode, onChange, readOnly]
  );

  const handleModeChange = (nextMode: DestinationMode): void => {
    if (readOnly || mode === nextMode) return;
    setMode(nextMode);
    setInputValue('');
    if (resolvedValue !== '') onChange('');
  };

  const handleOpen = useCallback(() => {
    if (canChoosePage) setIsOpen(true);
  }, [canChoosePage]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    chooseButtonRef.current?.focus();
  }, []);

  const handleSelect = useCallback(
    (option: DestinationOption) => {
      onChange(option.path);
      setInputValue(option.path);
      setIsOpen(false);
      chooseButtonRef.current?.focus();
    },
    [onChange]
  );

  return (
    <FieldLabel label={fieldLabel} readOnly={readOnly} el='div'>
      <div style={columnStyle}>
        <TabRow role='group' aria-label={`${fieldLabel} type`}>
          <button
            type='button'
            aria-pressed={mode === 'internal'}
            data-testid={`destination-tab-internal-${name}`}
            onClick={() => handleModeChange('internal')}
            disabled={readOnly}
          >
            Internal page
          </button>
          <button
            type='button'
            aria-pressed={mode === 'external'}
            data-testid={`destination-tab-external-${name}`}
            onClick={() => handleModeChange('external')}
            disabled={readOnly}
          >
            External URL
          </button>
        </TabRow>

        {mode === 'internal' ? (
          <>
            {enterpriseId && exactStoreCName && (
              <button
                ref={chooseButtonRef}
                type='button'
                onClick={handleOpen}
                disabled={readOnly}
                data-testid={`destination-choose-${name}`}
                style={chooseButtonStyle}
              >
                Choose Page
              </button>
            )}
            <label htmlFor={`destination-input-${name}`} style={{ ...columnStyle, ...hintStyle }}>
              Selected page
              <input
                type='text'
                value={inputValue}
                onChange={handleTextChange}
                disabled={readOnly}
                id={`destination-input-${name}`}
                placeholder={
                  exactStoreCName ? `e.g., /about-us, /stores/${exactStoreCName}, etc.` : 'e.g., /about-us, etc.'
                }
                data-testid={`destination-input-${name}`}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? errorId : undefined}
                style={error ? { ...inputStyle, borderColor: colors.error } : inputStyle}
              />
            </label>
          </>
        ) : (
          <>
            <input
              type='text'
              value={inputValue}
              onChange={handleTextChange}
              disabled={readOnly}
              placeholder='https://example.com'
              aria-label={`${fieldLabel} external URL`}
              data-testid={`destination-input-${name}`}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : undefined}
              style={error ? { ...inputStyle, borderColor: colors.error } : inputStyle}
            />
            <p style={hintStyle}>Full URL for destinations outside this storefront.</p>
          </>
        )}
        {error && (
          <span id={errorId} role='alert' style={errorStyle}>
            {error}
          </span>
        )}
      </div>

      {isOpen && enterpriseId && exactStoreCName && (
        <Suspense fallback={null}>
          <DestinationPickerModal
            key={exactStoreCName}
            enterpriseId={enterpriseId}
            cName={exactStoreCName}
            onSelect={handleSelect}
            onClose={handleClose}
          />
        </Suspense>
      )}
    </FieldLabel>
  );
}

export type DestinationFieldOptions = {
  /** LLM-facing instructions, matching `urlField`/`selectField` convention. */
  instructions?: string;
};

/**
 * Builds a Puck `custom` destination field with AI schema metadata.
 */
export function destinationField(label: string, options: DestinationFieldOptions = {}): CustomField<string> {
  const { instructions } = options;
  return {
    type: 'custom',
    label,
    render: DestinationField as CustomField<string>['render'],
    ai: {
      schema: { type: 'string' },
      stream: false,
      ...(instructions ? { instructions } : {}),
    },
  };
}
